import React from 'react';
import { Icon, Link, Stack, Text, useTheme } from '@fluentui/react';
import { IAdditionalData, IInstalledExtensionFolio } from '../../models/Extension';
import { ExtensionStaticDataForm, isRenderableStaticField } from '../ExtensionInstallation/ExtensionDataForm';
import { CRM_SETUP_STRINGS } from './wizardStrings';

// The wizard's field-mapping step: the per-sub-extension settings of whatever the user turned on.
//
// Required fields only by default. The full field list is one click away, but a first-time admin
// should meet three fields with a purpose, not thirty with a history. The shared field renderer is
// reused as-is (same as the credentials step), so option lookups, dependent fields, and protected
// handling stay in one place.
//
// The settings still waiting on a person are pinned into a highlighted band at the top, and drop
// into the ordinary list below as soon as they have a value. That ordering is what makes a screen of
// mostly-prefilled settings readable: the suggestions quietly shrink the band, so what is left in it
// is exactly the work remaining, and "am I done" is answered by whether the band is empty rather
// than by scrolling every group looking for a gap.

export interface ICrmMappingStepProps {
    folioData: IInstalledExtensionFolio;
    /** Identifiers of the sub-extensions the capabilities step enabled. */
    enabledSubExtensions: string[];
    /** Working values per sub-extension; the dialog owns this state. */
    subFields: { [identifier: string]: { [field: string]: string } };
    onChange: (identifier: string, data: { [field: string]: string }) => void;
}

/** The sub-extensions that actually have something to configure, with the fields to show. */
export const mappableSubExtensions = (
    folioData: IInstalledExtensionFolio | undefined, enabled: string[], showAll: boolean) => {
    const declared = folioData?.sourceExtensionFolio?.subExtensions ?? {};
    return enabled
        .map(identifier => {
            const sub = declared[identifier];
            // Only fields the form actually draws. A field it skips can never be answered here, so
            // counting one as outstanding produces a step that can never be finished.
            const fields = (sub?.additionalData ?? [])
                .filter(isRenderableStaticField)
                .filter(f => showAll || !f.optional);
            return sub ? { identifier, sub, fields } : undefined;
        })
        .filter((entry): entry is NonNullable<typeof entry> => !!entry && entry.fields.length > 0);
};

/** Whether the step has anything to show at all; the dialog skips it when not. */
export const hasMappableFields = (
    folioData: IInstalledExtensionFolio | undefined, enabled: string[]): boolean =>
    mappableSubExtensions(folioData, enabled, true).length > 0;

/** A required setting with nothing in it yet is the only thing that needs a person. */
const needsAnswer = (field: IAdditionalData, values: { [field: string]: string }): boolean =>
    !field.optional && (values[field.field] ?? '') === '';

const CrmMappingStep: React.FC<ICrmMappingStepProps> = (props) => {
    const theme = useTheme();
    const [showAll, setShowAll] = React.useState(false);

    const entries = mappableSubExtensions(props.folioData, props.enabledSubExtensions, showAll);
    const hiddenExist = !showAll &&
        mappableSubExtensions(props.folioData, props.enabledSubExtensions, true)
            .some(entry => entry.fields.length >
                (entries.find(e => e.identifier === entry.identifier)?.fields.length ?? 0));

    // Split every group into what still needs an answer and what does not. A field crosses over when
    // it gains a value; because the shared renderer reports changes on blur, that move happens after
    // the user has left the field rather than under their cursor.
    const split = entries.map(entry => {
        const values = props.subFields[entry.identifier] ?? {};
        return {
            ...entry,
            outstanding: entry.fields.filter(f => needsAnswer(f, values)),
            settled: entry.fields.filter(f => !needsAnswer(f, values)),
        };
    });

    const outstandingCount = split.reduce((total, entry) => total + entry.outstanding.length, 0);

    const renderForm = (entry: typeof split[number], fields: IAdditionalData[]) => (
        <ExtensionStaticDataForm
            staticData={props.subFields[entry.identifier] ?? {}}
            additionalFields={fields}
            extensionId={`folio_${props.folioData.sourceExtensionFolio?.id ?? ''}_${entry.identifier}`}
            updateOnDisengage
            onChange={(data) => props.onChange(entry.identifier, data)}
        />
    );

    /** The sub-extension's name, so a field is attributable to the feature that wants it. */
    const groupLabel = (entry: typeof split[number], show: boolean) => !show ? null : (
        <Text variant="small" styles={{ root: { fontWeight: 600, color: theme.palette.neutralSecondary } }}>
            {entry.sub.name ?? entry.identifier}
        </Text>
    );

    const outstandingGroups = split.filter(entry => entry.outstanding.length > 0);
    const settledGroups = split.filter(entry => entry.settled.length > 0);

    return (
        <Stack tokens={{ childrenGap: '1em' }}>
            <Text variant="small">{CRM_SETUP_STRINGS.mappingIntro}</Text>

            {outstandingCount > 0 && (
                <Stack
                    tokens={{ childrenGap: '0.75em' }}
                    styles={{
                        root: {
                            background: theme.palette.themeLighterAlt,
                            border: `1px solid ${theme.palette.themeLighter}`,
                            borderLeft: `3px solid ${theme.palette.themePrimary}`,
                            borderRadius: 2,
                            padding: '1em',
                        },
                    }}
                >
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: '0.5em' }}>
                        {/* An asterisk, not a pencil: this band is a label, and a pencil is the app's
                            clickable edit affordance everywhere else. */}
                        <Icon iconName="FieldRequired" styles={{ root: { color: theme.palette.themePrimary } }} />
                        <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
                            {CRM_SETUP_STRINGS.mappingNeedsYou(outstandingCount)}
                        </Text>
                    </Stack>
                    {outstandingGroups.map(entry => (
                        <Stack key={entry.identifier} tokens={{ childrenGap: '0.25em' }}>
                            {/* Always named here, even when only one group is outstanding. Pulling a
                                field out of its group is the point of this band, and the cost is that
                                "Company" on its own says nothing about which feature wants it: a lone
                                remaining field reads as unrelated to everything on screen. */}
                            {groupLabel(entry, true)}
                            {renderForm(entry, entry.outstanding)}
                        </Stack>
                    ))}
                </Stack>
            )}

            {outstandingCount === 0 && entries.length > 0 && (
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: '0.5em' }}>
                    <Icon iconName="CheckMark" styles={{ root: { color: theme.palette.themePrimary } }} />
                    <Text variant="small">{CRM_SETUP_STRINGS.mappingNothingOutstanding}</Text>
                </Stack>
            )}

            {entries.length === 0 && (
                <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    {CRM_SETUP_STRINGS.mappingNothingRequired}
                </Text>
            )}

            {settledGroups.length > 0 && (
                <Stack tokens={{ childrenGap: '0.75em' }}>
                    {outstandingCount > 0 && (
                        <Text variant="small" styles={{ root: { fontWeight: 600, color: theme.palette.neutralSecondary } }}>
                            {CRM_SETUP_STRINGS.mappingAlreadySet}
                        </Text>
                    )}
                    {settledGroups.map(entry => (
                        <Stack key={entry.identifier} tokens={{ childrenGap: '0.25em' }}>
                            {groupLabel(entry, settledGroups.length > 1)}
                            {renderForm(entry, entry.settled)}
                        </Stack>
                    ))}
                </Stack>
            )}

            {(hiddenExist || showAll) && (
                <Link onClick={() => setShowAll(!showAll)}>
                    {showAll ? CRM_SETUP_STRINGS.mappingRequiredOnly : CRM_SETUP_STRINGS.mappingShowAll}
                </Link>
            )}
        </Stack>
    );
};

export default CrmMappingStep;
